import { StakingEnrollmentStatus } from '@app/codegen';
import { StakingEnrollmentsLocators } from '@app/e2e/screens/StakingEnrollments';
import { Accordion } from '@app/shared/components';
import {
	AppIcon,
	AppSelector,
	datePickerMaxDate,
	datePickerMinDate,
	datePickerService,
	dateTimePlaceholder,
	humanize,
	ScreenSizeEnum,
	useAppDevice,
	useAppTheme,
} from '@itrustcapital/ui';
import { Button, Datepicker, Input } from '@ui-kitten/components';
import React from 'react';
import { Controller } from 'react-hook-form';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { useStakingEnrollmentsScreenContext } from '../StakingEnrollmentsScreenProvider';

export function StakingEnrollmentsFilterForm() {
	const styles = useCustomStyles();
	const context = useStakingEnrollmentsScreenContext();
	const { reset, clearErrors, handleSubmit } = context.filterStakingEnrollmentsForm;

	const onResetFilters = () => {
		reset();
		clearErrors();
		context.tableState.where.set(undefined);
		context.pauseQuery.set(false);
	};

	const onApplyFilters = handleSubmit(context.onSubmit, (error) => {
		console.error('Apply filters error: ', error);
	});

	return (
		<>
			<View style={styles.main.searchRow}>
				<Controller
					control={context.filterStakingEnrollmentsForm.control}
					name="search"
					render={(control) => (
						<Input
							accessoryRight={() => (
								<TouchableOpacity
									testID={
										StakingEnrollmentsLocators.stakingEnrollmentsSearchIconButton
									}
									onPress={handleSubmit(context.onSubmit)}
								>
									<AppIcon lib="fa5" name="search" size={18} />
								</TouchableOpacity>
							)}
							placeholder="Search by Enrollment ID, Account Number, Email"
							style={styles.main.inputHalves}
							testID={StakingEnrollmentsLocators.stakingEnrollmentsSearchField}
							value={control.field.value}
							onBlur={control.field.onBlur}
							onChangeText={control.field.onChange}
							onSubmitEditing={handleSubmit(context.onSubmit)}
						/>
					)}
				/>
				<Controller
					control={context.filterStakingEnrollmentsForm.control}
					name="status"
					render={(control) => (
						<View style={styles.main.inputHalves}>
							<AppSelector
								closeOnSelect
								data={[
									StakingEnrollmentStatus.CANCELED,
									StakingEnrollmentStatus.COMPLETED,
									StakingEnrollmentStatus.ENROLLED,
									StakingEnrollmentStatus.FAILED,
									StakingEnrollmentStatus.STAKED,
									StakingEnrollmentStatus.UNSTAKED,
								]}
								itemDisplay={(value) => humanize(value)}
								placeholder="Status"
								testID={StakingEnrollmentsLocators.stakingEnrollmentsStatusField}
								value={control.field.value}
								onSelect={(value) => {
									control.field.onChange(value || undefined);
									context.filterStakingEnrollmentsForm.handleSubmit(
										context.onSubmit
									)();
								}}
							/>
						</View>
					)}
				/>
			</View>
			<Accordion
				label="advanced filters"
				triggerContainerStyle={styles.advanced.triggerContainer}
			>
				<View style={styles.advanced.inputRow}>
					<Controller
						control={context.filterStakingEnrollmentsForm.control}
						name="createdAt"
						render={(control) => (
							<Datepicker
								date={control.field.value}
								dateService={datePickerService}
								label="Created At"
								max={datePickerMaxDate}
								min={datePickerMinDate}
								placeholder={dateTimePlaceholder}
								status={control.fieldState.error && 'danger'}
								style={styles.advanced.control}
								testID={StakingEnrollmentsLocators.stakingEnrollmentsCreatedAtField}
								onBlur={control.field.onBlur}
								onSelect={control.field.onChange}
							/>
						)}
					/>
				</View>

				<View style={[styles.main.row, styles.main.buttons]}>
					<Button
						style={styles.advanced.apply}
						testID={StakingEnrollmentsLocators.stakingEnrollmentsApplyButton}
						onPress={() => {
							clearErrors();
							onApplyFilters();
						}}
					>
						Apply Filters
					</Button>
					<Button
						appearance="outline"
						testID={StakingEnrollmentsLocators.stakingEnrollmentsClearButton}
						onPress={onResetFilters}
					>
						Clear Filters
					</Button>
				</View>
			</Accordion>
		</>
	);
}

function useCustomStyles() {
	const theme = useAppTheme();
	const { width } = useAppDevice();

	const isScreenMd = width >= ScreenSizeEnum.md;

	return {
		main: StyleSheet.create({
			container: {
				flex: 1,
				justifyContent: 'center',
				alignItems: 'center',
				textTransform: 'capitalize',
			},
			buttons: {
				paddingVertical: 4,
			},
			row: {
				flexDirection: 'row',
			},
			inputHalves: {
				flex: 0.49,
			},
			searchRow: {
				flexDirection: isScreenMd ? 'row' : 'column',
				justifyContent: isScreenMd ? 'space-between' : 'center',
			},
		}),
		header: StyleSheet.create({
			container: {
				padding: 8,
				justifyContent: 'space-between',
				flexDirection: 'row',
				flexWrap: 'wrap',
				alignItems: 'center',
			},
			exportIcon: {
				color: theme['text-info-color'],
			},
			text: {
				textTransform: 'capitalize',
			},
		}),
		advanced: StyleSheet.create({
			apply: {
				marginRight: 12,
			},
			control: {
				width: isScreenMd ? '35%' : 'auto',
				maxWidth: 330,
				marginRight: isScreenMd ? 8 : 0,
				paddingVertical: 8,
			},
			inputRow: {
				flexDirection: isScreenMd ? 'row' : 'column',
				justifyContent: isScreenMd ? 'flex-start' : 'center',
			},
			triggerContainer: {
				flexDirection: 'row',
				justifyContent: 'flex-start',
				marginVertical: 8,
			},
		}),
	};
}
